import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, EVENT_LABEL } from "../lib/api";
import { fmtTime } from "../lib/format";
import { useAuth } from "../auth";
import { Icon } from "./icons";

const DISMISS_KEY = "ulc_reminders_dismissed";
const NOTIFIED_KEY = "ulc_reminders_notified";

function load(key) {
  try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; }
}
function save(key, arr) {
  try { localStorage.setItem(key, JSON.stringify(arr.slice(-100))); } catch (err) { console.error("Falha ao guardar lembretes", err); }
}

function isConvoked(e, userId) {
  if (!Array.isArray(e.member_ids) || e.member_ids.length === 0) return true; // whole choir
  return e.member_ids.map(String).includes(String(userId));
}

/** In-app reminder: events the member is convoked to that start within ~36h. */
export function ReminderBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => load(DISMISS_KEY));
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const end = new Date(now.getFullYear(), now.getMonth() + 2, 1).toISOString();
  const q = useQuery({
    queryKey: ["events-reminders", start, end],
    queryFn: () => api(`/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });

  const upcoming = useMemo(() => {
    const t = Date.now();
    return (q.data || [])
      .filter((e) => e.starts_at && isConvoked(e, user?.id))
      .map((e) => ({ ...e, _in: new Date(e.starts_at).getTime() - t }))
      .filter((e) => e._in > 0 && e._in <= 36 * 3600 * 1000)
      .sort((a, b) => a._in - b._in);
  }, [q.data, user?.id]);

  // Fire a browser notification once per event when within 24h (only while the app is open).
  useEffect(() => {
    if (perm !== "granted" || typeof Notification === "undefined") return;
    const notified = load(NOTIFIED_KEY);
    let changed = false;
    upcoming.forEach((e) => {
      if (e._in <= 24 * 3600 * 1000 && !notified.includes(e.id)) {
        try {
          new Notification("Urban Lisbon Choir", {
            body: `${EVENT_LABEL[e.type] || "Evento"}: ${e.title} — ${new Date(e.starts_at).toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" })} às ${fmtTime(e.starts_at)}`,
            icon: "/icons/icon-192.png",
            tag: `ulc-${e.id}`,
          });
          notified.push(e.id);
          changed = true;
        } catch (err) {
          console.error("Falha ao mostrar notificação", err);
        }
      }
    });
    if (changed) save(NOTIFIED_KEY, notified);
  }, [upcoming, perm]);

  const visible = upcoming.filter((e) => !dismissed.includes(e.id));
  if (!visible.length) return null;
  const e = visible[0];
  const d = new Date(e.starts_at);
  const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const evMid = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((evMid - todayMid) / (24 * 3600 * 1000));
  const when = days <= 0 ? "hoje" : days === 1 ? "amanhã" : d.toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" });

  const dismiss = () => { const next = [...dismissed, e.id]; setDismissed(next); save(DISMISS_KEY, next); };
  const enable = async () => {
    try { setPerm(await Notification.requestPermission()); } catch (err) { console.error("Permissão de notificações falhou", err); }
  };

  return (
    <div data-testid="reminder-banner" style={{ background: "var(--brand-tertiary)", borderBottom: "1px solid var(--brand-primary)", padding: "10px 16px", display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ width: 34, height: 34, borderRadius: 999, background: "var(--brand-primary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name="bell" size={16} color="var(--on-brand)" />
      </span>
      <button onClick={() => navigate("/calendar")} style={{ flex: 1, minWidth: 0, background: "none", border: "none", textAlign: "left", cursor: "pointer", color: "inherit" }}>
        <div style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>
          {(EVENT_LABEL[e.type] || "Evento")} {when} às {fmtTime(e.starts_at)}
        </div>
        <div className="t-hint" style={{ color: "var(--on-surface-3)" }}>
          {e.title}{e.location ? ` · ${e.location}` : ""}{visible.length > 1 ? ` · +${visible.length - 1}` : ""}
        </div>
      </button>
      {perm === "default" ? (
        <button onClick={enable} data-testid="reminder-enable" className="chip" style={{ height: 32 }}>ATIVAR AVISOS</button>
      ) : null}
      <button className="icon-btn" style={{ width: 34, height: 34 }} onClick={dismiss} data-testid="reminder-dismiss"><Icon name="close" size={16} color="var(--on-surface-3)" /></button>
    </div>
  );
}
